import React, { useCallback, useEffect, useMemo, useState } from 'react';

function todayYmd() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function addDaysYmd(ymd, delta) {
  const m = String(ymd || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return ymd;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  d.setDate(d.getDate() + delta);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function defaultRange(grain) {
  const to = todayYmd();
  if (grain === 'day') return { from: addDaysYmd(to, -13), to };
  if (grain === 'month') return { from: addDaysYmd(to, -151), to };
  return { from: addDaysYmd(to, -41), to };
}

function fmtQty(n) {
  if (n == null || !Number(n)) return '';
  const x = Math.round((Number(n) + Number.EPSILON) * 10) / 10;
  return String(x).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
}

function fmtAmt(n) {
  const x = Math.round(Number(n) || 0);
  if (!x) return '';
  return `$${x.toLocaleString('zh-TW')}`;
}

function salesToken(auth) {
  try {
    if (auth === 'shift') return localStorage.getItem('shift_token') || '';
    return localStorage.getItem('admin_token') || '';
  } catch { return ''; }
}

async function salesGet(path, auth) {
  const res = await fetch(path, {
    headers: { Authorization: `Bearer ${salesToken(auth)}` },
  });
  return res.json();
}

function emptyCell() {
  return { saleQty: 0, saleAmt: 0, mktQty: 0, mktAmt: 0, scrapQty: 0, scrapAmt: 0 };
}

function sumRowCells(rows, bucketCount) {
  const cells = Array.from({ length: bucketCount }, emptyCell);
  for (const r of rows || []) {
    (r.cells || []).forEach((c, i) => {
      if (!cells[i]) return;
      cells[i].saleQty += Number(c.saleQty) || 0;
      cells[i].saleAmt += Number(c.saleAmt) || 0;
      cells[i].mktQty += Number(c.mktQty) || 0;
      cells[i].mktAmt += Number(c.mktAmt) || 0;
      cells[i].scrapQty += Number(c.scrapQty) || 0;
      cells[i].scrapAmt += Number(c.scrapAmt) || 0;
    });
  }
  return cells;
}

function CellView({ c }) {
  const sale = fmtQty(c?.saleQty);
  const mkt = fmtQty(c?.mktQty);
  const scrap = fmtQty(c?.scrapQty);
  const amt = fmtAmt((c?.saleAmt || 0) + (c?.mktAmt || 0) + (c?.scrapAmt || 0));
  if (!sale && !mkt && !scrap && !amt) return null;
  return (
    <div style={{ lineHeight: 1.25 }}>
      {sale ? <div style={{ fontWeight: 800, fontSize: 15, color: '#0f172a' }}>{sale}</div> : null}
      {(mkt || scrap) ? (
        <div style={{ display: 'flex', gap: 8, fontSize: 11, fontWeight: 700, marginTop: 2 }}>
          {mkt ? <span style={{ color: '#0369a1' }}>銷 {mkt}</span> : null}
          {scrap ? <span style={{ color: '#b45309' }}>廢 {scrap}</span> : null}
        </div>
      ) : null}
      {amt ? <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{amt}</div> : null}
    </div>
  );
}

export default function SalesConsumptionView({ auth = 'admin', compact = false }) {
  const [grain, setGrain] = useState('week');
  const [from, setFrom] = useState(() => defaultRange('week').from);
  const [to, setTo] = useState(() => defaultRange('week').to);
  const [groups, setGroups] = useState([]);
  const [names, setNames] = useState([]);
  const [by, setBy] = useState('group');
  const [group, setGroup] = useState('');
  const [name, setName] = useState('');
  const [nameQ, setNameQ] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await salesGet('/api/sales/items', auth);
        if (!cancelled && r?.ok) {
          setGroups(r.groups || []);
          setNames(r.names || []);
        }
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [auth]);

  const nameOpts = useMemo(() => {
    const kw = nameQ.trim().toLowerCase();
    const list = kw ? names.filter((n) => n.toLowerCase().includes(kw)) : names;
    if (name && !list.includes(name)) return [name, ...list];
    return list;
  }, [names, nameQ, name]);

  const load = useCallback(async () => {
    if (!from || !to || from > to) {
      setErr('請選正確日期');
      setData(null);
      return;
    }
    setLoading(true); setErr('');
    try {
      const qs = new URLSearchParams({ from, to, grain });
      if (by === 'name') {
        if (name) qs.set('name', name);
      } else if (group) {
        qs.set('group', group);
      }
      const r = await salesGet(`/api/sales/consumption?${qs}`, auth);
      if (!r?.ok) throw new Error(r?.message || r?.error || '載入失敗');
      setData(r);
    } catch (e) {
      setErr(e.message || '載入失敗');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [auth, from, to, grain, by, group, name]);

  useEffect(() => { load(); }, [load]);

  const onGrain = (g) => {
    const r = defaultRange(g);
    setGrain(g);
    setFrom(r.from);
    setTo(r.to);
  };

  const totals = useMemo(
    () => sumRowCells(data?.rows || [], (data?.buckets || []).length),
    [data],
  );

  const inp = {
    padding: compact ? '6px 8px' : '8px 10px',
    border: '1px solid #d1d5db', borderRadius: 8, fontSize: compact ? 13 : 14,
    background: '#fff',
  };

  return (
    <div style={{ padding: compact ? 8 : 0, background: compact ? '#fff' : undefined, borderRadius: compact ? 12 : 0 }}>
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 12,
      }}>
        {['day', 'week', 'month'].map((g) => (
          <button
            key={g}
            type="button"
            onClick={() => onGrain(g)}
            style={{
              padding: compact ? '6px 10px' : '8px 14px', border: 'none', borderRadius: 8,
              fontWeight: 700, cursor: 'pointer',
              background: grain === g ? '#1e293b' : '#e2e8f0',
              color: grain === g ? '#fff' : '#475569',
            }}
          >
            {g === 'day' ? '日' : g === 'week' ? '週' : '月'}
          </button>
        ))}
        {['group', 'name'].map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => {
              setBy(k);
              setGroup('');
              setName('');
              setNameQ('');
            }}
            style={{
              padding: compact ? '6px 10px' : '8px 14px', border: 'none', borderRadius: 8,
              fontWeight: 700, cursor: 'pointer',
              background: by === k ? '#1e293b' : '#e2e8f0',
              color: by === k ? '#fff' : '#475569',
            }}
          >
            {k === 'group' ? '群組' : '品名'}
          </button>
        ))}
        {by === 'group' ? (
          <select
            value={group}
            onChange={(e) => setGroup(e.target.value)}
            style={{ ...inp, minWidth: 140 }}
          >
            <option value="">全部</option>
            {groups.map((g) => (
              <option key={g} value={g}>{g}</option>
            ))}
          </select>
        ) : (
          <>
            <input
              value={nameQ}
              onChange={(e) => setNameQ(e.target.value)}
              placeholder="篩選品名"
              style={{ ...inp, minWidth: 120, maxWidth: 180 }}
            />
            <select
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={{ ...inp, minWidth: 180 }}
            >
              <option value="">全部</option>
              {nameOpts.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </>
        )}
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={inp} />
        <span style={{ color: '#94a3b8' }}>～</span>
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={inp} />
      </div>

      {err ? <div style={{ color: '#b91c1c', marginBottom: 8 }}>{err}</div> : null}
      {loading && !data ? <div style={{ padding: 16, color: '#888' }}>載入中…</div> : null}

      {!loading && data && !(data.rows || []).length ? (
        <div style={{ padding: 16, color: '#94a3b8', fontSize: 13 }}>此區間無銷售</div>
      ) : null}

      {(data?.rows || []).length ? (
        <div style={{ overflow: 'auto', maxHeight: 'calc(100dvh - 220px)', border: '1px solid #eef2f7', borderRadius: 12, opacity: loading ? 0.6 : 1 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560, background: '#fff' }}>
            <thead>
              <tr>
                <th style={{ ...th, position: 'sticky', left: 0, top: 0, background: '#fff', zIndex: 3 }}>品項</th>
                {(data.buckets || []).map((b) => (
                  <th key={b.key} style={th}>{b.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r, ri) => (
                <tr key={`${r.group}-${r.name}`} style={{ background: ri % 2 ? '#fafbfc' : '#fff' }}>
                  <td style={{
                    ...td,
                    position: 'sticky', left: 0, background: ri % 2 ? '#fafbfc' : '#fff', zIndex: 1,
                    minWidth: 120,
                  }}>
                    <div style={{ fontWeight: 800, color: '#0f172a' }}>{r.name}</div>
                    {by === 'group' && r.group ? (
                      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{r.group}</div>
                    ) : null}
                  </td>
                  {(r.cells || []).map((c, i) => (
                    <td key={data.buckets[i]?.key || i} style={{ ...td, minWidth: 72 }}>
                      <CellView c={c} />
                    </td>
                  ))}
                </tr>
              ))}
              <tr style={{ background: '#f8fafc' }}>
                <td style={{
                  ...td, position: 'sticky', left: 0, background: '#f8fafc', zIndex: 1,
                  fontWeight: 800,
                }}>
                  合計
                </td>
                {totals.map((c, i) => (
                  <td key={`sum-${data.buckets[i]?.key || i}`} style={{ ...td, minWidth: 72 }}>
                    <CellView c={c} />
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}

const th = {
  textAlign: 'left', padding: '10px 12px', fontSize: 12, color: '#64748b',
  borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap', fontWeight: 700,
  background: '#fff',
  position: 'sticky', top: 0, zIndex: 2,
};
const td = {
  padding: '10px 12px', fontSize: 13, color: '#1f2937',
  borderBottom: '1px solid #f1f5f9',
  verticalAlign: 'top',
};
